import { PageFrame, Tabs, TabsContent, TabsList, TabsTrigger } from '@eo/shared';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '../../app/store';
import { AccountPanel } from './AccountPanel';
import { AppearanceControl } from './AppearanceControl';
import { SETTINGS_TABS } from './constants';
import { parseTab } from './helpers';
import { IntegrationsPanel } from './IntegrationsPanel';
import { useOAuthReturn } from './useOAuthReturn';
import type { SettingsContentProps } from './types';

export function SettingsContent({ data, ...actions }: SettingsContentProps) {
  const me = useAppSelector((s) => s.staff.me);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get('tab'));
  useOAuthReturn();

  const onTabChange = (next: string) => {
    setSearchParams(
      (params) => {
        if (next === 'account') params.delete('tab');
        else params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };

  return (
    // The frame sits inside <Tabs> so the tab list can live in its control row
    // while the panels stay under the same Tabs context.
    <Tabs value={tab} onValueChange={onTabChange} className="flex flex-col">
      <PageFrame
        title="Settings"
        controls={
          <div className="flex w-full flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <TabsList variant="underline" className="w-full xl:w-auto">
              {SETTINGS_TABS.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  className="h-12 flex-1 xl:h-8 xl:flex-initial"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex sm:justify-end">
              <AppearanceControl />
            </div>
          </div>
        }
      />

      <div className="px-[var(--page-margin)] pb-8">
        <TabsContent value="account">{me ? <AccountPanel me={me} /> : null}</TabsContent>
        <TabsContent value="integrations">
          <IntegrationsPanel data={data} {...actions} />
        </TabsContent>
      </div>
    </Tabs>
  );
}
